// Single source of truth for agent posting locations.
//
// These `value` strings ARE the stored data. The backend declares
// `admin.location` and `booking.location` / `booking.finishedAt` as free-form
// Strings with no enum, and the bookings report filters them with an exact
// `$in` match. So a posting assigned to an agent is copied verbatim onto every
// booking they finish, and the filter only finds it by exact string equality.
//
// Add new postings here; never rename an existing `value` — that orphans every
// admin and booking already carrying the old string. (Labels are display-only
// and safe to edit.)

export type LocationOption = {
  value: string
  label: string
  /**
   * Retired postings are no longer assignable, but historical bookings still
   * carry them, so they stay in the filters and remain selectable for an agent
   * already posted there (so the select reflects their current location and
   * they can be moved off it).
   */
  retired?: boolean
}

export const STAFF_LOCATIONS: LocationOption[] = [
  { value: 'HQ', label: 'HQ' },
  { value: 'Ajao Estate', label: 'Ajao Estate' },
  { value: 'Ikosi', label: 'Ikosi' },
  { value: 'Lekki', label: 'Lekki' },
  { value: 'NAHCO', label: 'NAHCO', retired: true },
  { value: 'Stop Over', label: 'Stop Over' },
  { value: 'Welcome Centre', label: 'Welcome Centre' },
]

/** Postings offered when assigning an agent, plus their own if it is retired. */
export const assignableStaffLocations = (
  currentLocation?: string,
): LocationOption[] =>
  STAFF_LOCATIONS.filter((loc) => !loc.retired || loc.value === currentLocation)

/**
 * Location options for the bookings filters. Includes every posting (retired
 * ones too, since past bookings carry them) plus `direct customer`, which the
 * backend writes for bookings created from a customer draft rather than by an
 * agent at a station.
 */
export const BOOKING_LOCATION_FILTERS: LocationOption[] = [
  { value: '', label: 'All' },
  { value: 'direct customer', label: 'Direct Customer' },
  ...STAFF_LOCATIONS,
]
